import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Radio, Search, Workflow } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import CreateAutomationModal from './CreateAutomationModal';
import type { AutomationStatus, AutomationWithCounts } from './types';

const STATUS_STYLE: Record<AutomationStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  archived: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function formatWhen(value: unknown): string {
  if (typeof value !== 'string') return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

export default function AutomationsView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AutomationWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  /** Realtime reloads pass silent so the table does not flash a spinner mid-read. */
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // List + per-row aggregate counts. Done in two trips because PostgREST
      // can't do a left-join with grouped counts cleanly without a view.
      const { data: automations, error } = await supabase
        .from('automations')
        .select('*')
        .order('status', { ascending: true })
        .order('updated_at', { ascending: false });
      if (error) throw error;

      const ids = (automations ?? []).map((a) => a.id as string);
      const [stepsRes, enrollsRes] = await Promise.all([
        ids.length > 0
          ? supabase
              .from('automation_steps')
              .select('automation_id')
              .in('automation_id', ids)
          : Promise.resolve({ data: [], error: null } as { data: unknown[]; error: null }),
        ids.length > 0
          ? supabase
              .from('automation_enrollments')
              .select('automation_id, status')
              .in('automation_id', ids)
          : Promise.resolve({ data: [], error: null } as { data: unknown[]; error: null }),
      ]);
      if ('error' in stepsRes && stepsRes.error) throw stepsRes.error;
      if ('error' in enrollsRes && enrollsRes.error) throw enrollsRes.error;

      const stepCounts = new Map<string, number>();
      for (const r of (stepsRes.data ?? []) as Array<{ automation_id: string }>) {
        stepCounts.set(r.automation_id, (stepCounts.get(r.automation_id) ?? 0) + 1);
      }
      const enrollCounts = new Map<string, number>();
      for (const r of (enrollsRes.data ?? []) as Array<{ automation_id: string; status: string }>) {
        if (r.status === 'active') {
          enrollCounts.set(r.automation_id, (enrollCounts.get(r.automation_id) ?? 0) + 1);
        }
      }

      setRows(
        (automations ?? []).map((a) => ({
          ...(a as AutomationWithCounts),
          step_count: stepCounts.get(a.id as string) ?? 0,
          active_enrollment_count: enrollCounts.get(a.id as string) ?? 0,
        })),
      );
    } catch (e: unknown) {
      reportUiError('automations', 'list', e);
      const msg = e instanceof Error ? e.message : 'Failed to load automations';
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live list: the row counts come from three tables, and any of them can change from
  // outside this tab (Engine MCP, the cron, another window). Reload on all three,
  // debounced — a step reorder writes every row twice and would otherwise refetch
  // once per update.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    let timer: number | undefined;
    const reload = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void loadRef.current(true);
      }, 300);
    };

    const channel = supabase.channel('automations:list');
    for (const table of ['automations', 'automation_steps', 'automation_enrollments']) {
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, reload);
    }
    channel.subscribe((status) => {
      setLive(status === 'SUBSCRIBED');
    });

    return () => {
      if (timer) window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.alias?.toLowerCase().includes(q) ?? false) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  }, [rows, searchQuery]);

  const handleCreate = () => {
    setCreateModalOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <CreateAutomationModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(id) => navigate(`/automations/${id}`)}
      />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Workflow className="w-8 h-8 mr-3 text-emerald-500" />
            Automations
            {live ? (
              <span
                title="Live updates connected"
                className="hidden sm:inline-flex items-center gap-1 ml-3 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
              >
                <Radio className="w-3 h-3" />
                Live
              </span>
            ) : null}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manually enroll subscribers; the cron sends each step at the right time.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, alias..."
              className="pl-10 pr-4 py-2.5 w-56 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center justify-center p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            title="New automation"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <Workflow className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {searchQuery ? 'No matching automations' : 'No automations yet'}
          </h3>
          {!searchQuery ? (
            <>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Create one and add a few steps to start scheduling emails on enrollment.
              </p>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Create your first automation
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Alias
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Steps
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Active enrollments
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/automations/${a.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/automations/${a.id}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-900/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {a.name || '(untitled)'}
                      </div>
                      {a.description ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                          {a.description}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 font-mono whitespace-nowrap">
                      {a.alias ?? <span className="text-gray-400 italic font-sans">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${STATUS_STYLE[a.status]}`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">
                      {a.step_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">
                      {a.active_enrollment_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatWhen(a.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
